/**
 * Phase 6-C / Journey B — Client Portal Magic Link landing.
 *
 * The agency emails a URL like `/client-portal/auth?token=<jwt-or-opaque>`.
 * On mount we POST it to `/api/v1/client-portal/auth`. On success we store
 * the returned access token + identity in Redux (`auth.clientPortal.*`) and
 * navigate to the client dashboard. On failure we send the user to the
 * `/client-portal/expired` page.
 *
 * The magic-link token is single-use server-side; the access token returned
 * here lives in Redux ONLY (never localStorage) per CLAUDE.md.
 *
 * Visually this is a friendly, brand-neutral "signing you in" screen — the
 * client hasn't been identified yet, so it stays neutral until the dashboard.
 */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { authenticate } from '../../lib/api/client-portal/client-portal-api';
import { setClientPortalSession } from '../../store/reducers/authSlice';
import { PORTAL_NEUTRAL } from './brand';

const ClientPortalMagicLink: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('This sign-in link is missing its access code.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticate(token);
        if (cancelled) return;
        dispatch(
          setClientPortalSession({
            accessToken: res.access_token,
            expiresAt: res.expires_at,
            identity: res.client,
          })
        );
        navigate('/client-portal/dashboard', { replace: true });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ClientPortalMagicLink] auth failed', err);
        if (!cancelled) {
          // Redirect to a dedicated expired page so the back-button-history
          // doesn't keep the failing token in the URL.
          navigate('/client-portal/expired', { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f6f8',
        px: 2,
      }}
    >
      <Container maxWidth='xs' disableGutters>
        <Paper
          elevation={0}
          variant='outlined'
          sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}
        >
          {error ? (
            <Stack spacing={2}>
              <Typography variant='h6' sx={{ fontWeight: 700 }}>
                We couldn't sign you in
              </Typography>
              <Alert severity='error' sx={{ textAlign: 'left' }}>
                {error}
              </Alert>
              <Typography variant='body2' color='text.secondary'>
                Ask your agency contact to send you a fresh access link — or
                sign in with a one-time code instead.
              </Typography>
              <Button
                component={RouterLink}
                to='/client-portal/login'
                variant='contained'
                fullWidth
                sx={{
                  backgroundColor: PORTAL_NEUTRAL,
                  '&:hover': { backgroundColor: '#000' },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.1,
                }}
              >
                Sign in with a code
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2.5} alignItems='center'>
              <CircularProgress size={30} sx={{ color: PORTAL_NEUTRAL }} />
              <Box>
                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                  Signing you in…
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                  Verifying your secure link. This only takes a moment.
                </Typography>
              </Box>
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ClientPortalMagicLink;
